import api from '@/lib/api';

// Above this a single request body is refused by the proxy, so the file goes up
// in chunks instead. There is no upper limit on document size.
const DIRECT_LIMIT = 8 * 1024 * 1024;

/**
 * Upload contract documents of any format and any size.
 * `onProgress({ name, pct, i, of })` is called as each file moves.
 * Returns the document records that were saved.
 */
export async function uploadContractDocuments(contractId, files, { folderId, onProgress } = {}) {
  const list = Array.from(files || []);
  const saved = [];
  for (let i = 0; i < list.length; i += 1) {
    const f = list[i];
    const report = (pct) => onProgress?.({ name: f.name, pct, i: i + 1, of: list.length });
    report(0);
    if (f.size > DIRECT_LIMIT) {
      saved.push(await uploadInChunks(contractId, f, folderId, report));
    } else {
      const fd = new FormData();
      fd.append('files', f);
      const { data } = await api.post(`/contracts/contracts/${contractId}/documents`, fd, {
        onUploadProgress: (e) => e.total && report(Math.round((e.loaded / e.total) * 100)),
      });
      const docs = data?.documents || [];
      if (folderId && folderId !== 'unfiled') {
        await Promise.all(docs.map(d => api.post(`/contracts/contracts/${contractId}/documents/move`,
          { storage_path: d.storage_path, folder_id: folderId })));
      }
      saved.push(...docs);
    }
  }
  return saved;
}

async function uploadInChunks(contractId, f, folderId, report) {
  const { data: init } = await api.post(`/contracts/contracts/${contractId}/documents/upload/init`,
    { file_name: f.name, file_size: f.size, mime_type: f.type || 'application/octet-stream' });
  const size = init.chunk_size || 5 * 1024 * 1024;
  const total = Math.ceil(f.size / size);
  try {
    for (let i = 0; i < total; i += 1) {
      await api.put(
        `/contracts/contracts/${contractId}/documents/upload/${init.upload_id}/chunk?index=${i}`,
        f.slice(i * size, (i + 1) * size),
        { headers: { 'Content-Type': 'application/octet-stream' } });
      report(Math.round(((i + 1) / total) * 100));
    }
    const { data } = await api.post(
      `/contracts/contracts/${contractId}/documents/upload/${init.upload_id}/complete`,
      { folder_id: folderId || 'unfiled' });
    return data.document;
  } catch (e) {
    api.delete(`/contracts/contracts/${contractId}/documents/upload/${init.upload_id}`).catch(() => {});
    throw e;
  }
}
